//
// Copyright © 2026 Nester Labs
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { type Timestamp } from '@hcengineering/core'

/**
 * Deliberately not imported from @hcengineering/ui — this module is also loaded
 * by the server trigger, which must not pull in browser code.
 * @public
 */
export const MS_IN_DAY = 24 * 60 * 60 * 1000

/**
 * @public
 */
export const DAYS_IN_WEEK = 7

/**
 * Timestamps are bucketed into calendar days in UTC, and the grid writes entries
 * at UTC noon (see {@link getDayTimestamp}). Noon keeps a day's timestamp inside
 * the same calendar date for every offset from UTC-11 to UTC+11, so the client
 * and the transactor agree on which week an entry belongs to.
 *
 * Real offsets span UTC-12 to UTC+14 — wider than a day — so no single stamping
 * instant can cover all of them. Noon is the widest window available; UTC+12 and
 * beyond (NZ in DST, Kiribati, Samoa) read the following calendar date.
 *
 * Entries created by tracker's own "Add time report" popup carry a raw local
 * timestamp instead. Those land in the right week except when logged within a
 * few hours of midnight from a far-offset timezone.
 * @public
 */
export function getDayTimestamp (weekStart: Timestamp, dayIndex: number): Timestamp {
  return weekStart + dayIndex * MS_IN_DAY + MS_IN_DAY / 2
}

/**
 * Start of the UTC calendar day containing `ts`.
 * @public
 */
export function startOfDay (ts: Timestamp): Timestamp {
  return Math.floor(ts / MS_IN_DAY) * MS_IN_DAY
}

/**
 * Monday 00:00:00.000 UTC of the week containing `ts`.
 * @public
 */
export function getWeekStart (ts: Timestamp): Timestamp {
  const day = startOfDay(ts)
  // getUTCDay: 0 = Sunday .. 6 = Saturday; we want Monday as index 0
  const dayOfWeek = (new Date(day).getUTCDay() + 6) % DAYS_IN_WEEK
  return day - dayOfWeek * MS_IN_DAY
}

/**
 * Monday 00:00:00.000 and Sunday 23:59:59.999 UTC around `ts`.
 * @public
 */
export function getWeekBounds (ts: Timestamp): { weekStart: Timestamp, weekEnd: Timestamp } {
  const weekStart = getWeekStart(ts)
  return { weekStart, weekEnd: weekStart + DAYS_IN_WEEK * MS_IN_DAY - 1 }
}

/**
 * Index of the day `ts` falls on within its week, 0 = Monday .. 6 = Sunday.
 * @public
 */
export function getDayIndex (ts: Timestamp): number {
  return Math.floor((startOfDay(ts) - getWeekStart(ts)) / MS_IN_DAY)
}

/**
 * @public
 */
export function shiftWeek (weekStart: Timestamp, weeks: number): Timestamp {
  return weekStart + weeks * DAYS_IN_WEEK * MS_IN_DAY
}

/**
 * Hours carry one decimal everywhere — the grid, the denormalized period totals
 * and the export. Without this, repeated float addition drifts (0.1 + 0.2).
 * @public
 */
export function roundHours (value: number): number {
  return Math.round(value * 100) / 100
}
